import { NUMBER_MASK } from '@/modules/projects/components/Workbench/accounts'
import { formatDate } from '@/utils'
import { monthLabel } from './model/months'
import {
  type AccountAsOf,
  isReadWithoutAccount,
} from './model/portfolioOverview'
import type { DateWindow, MonthKey } from './model/types'

const COMPUTED = 'computed'

// "Printed" only for the statement's own figure as is. A computed figure never says printed.
export const securitiesSourceNote = (account: AccountAsOf): string =>
  account.statement?.printedHoldingsTotal != null ? 'Printed total' : COMPUTED

export const cashSourceNote = (account: AccountAsOf): string | null => {
  if (account.cashSource === 'computed') return COMPUTED
  if (account.cashSource !== 'printed') return null
  return (account.cash ?? 0) < 0
    ? 'Printed balance, below zero'
    : 'Printed balance'
}

const yearOf = (calendarDate: string) => calendarDate.slice(0, 4)

// "Dec 31, 2022"
export const longDate = (calendarDate: string): string =>
  formatDate(calendarDate, 'MMM d, yyyy')

// "Sep 1 to Dec 31, 2022", "Dec 1 to 31, 2022", "Apr 1, 2022 to Dec 31, 2025"
export const dateRangeText = ({ from, to }: DateWindow): string => {
  if (yearOf(from) !== yearOf(to)) return `${longDate(from)} to ${longDate(to)}`
  const sameMonth = from.slice(0, 7) === to.slice(0, 7)
  const end = formatDate(to, sameMonth ? 'd, yyyy' : 'MMM d, yyyy')
  return `${formatDate(from, 'MMM d')} to ${end}`
}

// "Sep 30" when it falls in the as-of year, else "Mar 31, 2022".
export const sinceText = (since: string, asOfDate: string): string =>
  yearOf(since) === yearOf(asOfDate)
    ? formatDate(since, 'MMM d')
    : longDate(since)

// "Dec 2022", "Sep to Dec 2022", "Jan 2025 to May 2026"
export const monthSpanText = (first: MonthKey, last: MonthKey): string => {
  if (first === last) return monthLabel(first)
  if (yearOf(first) !== yearOf(last)) {
    return `${monthLabel(first)} to ${monthLabel(last)}`
  }
  return `${formatDate(`${first}-01`, 'MMM')} to ${monthLabel(last)}`
}

// "1 account", "3 accounts"
export const countOf = (count: number, noun: string, nouns = `${noun}s`) =>
  `${count.toLocaleString()} ${count === 1 ? noun : nouns}`

// "A", "A and B", "A, B and C"
export const joinWithAnd = (items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

// For a failed_read month. No end stop, so a cell and a sentence can both use it.
export const unreadStatementText = (
  account: Pick<AccountAsOf, 'month' | 'last4' | 'badge'>,
): string => {
  const statement = `The ${monthLabel(account.month)} statement`
  return isReadWithoutAccount(account.badge.reasons, account.month)
    ? `${statement} was read without account ${NUMBER_MASK} ${account.last4}`
    : `${statement} could not be read`
}

// Said in place of "No deposits ..." when the only money is on rows typed Other.
export const otherActivitySentence = (count: number): string =>
  `${countOf(count, 'row')} of other activity not counted.`
